import { Box, Paper, Stack, TextField, Typography, useTheme } from "@mui/material";
import { SwapHorizRounded } from "@mui/icons-material";
import { useEffect, useState } from "react";
import apiService from "../../../../../../../service/Api";
import TemplatePickerDialog, { IntroductionLetterTemplate, ProposalData, replacePlaceholders, stripHtml } from "./TemplatePickerDialog";
import { tokens } from "../../../../../../../theme";


interface IntroductionLetterSectionProps {
  bookingType?: string;
  proposalData?: ProposalData;
  onChange?: (value: string) => void;
  initialValue?:string;
}

export default function IntroductionLetterSection({
  bookingType = "Funeral",
  proposalData,
  onChange,
  initialValue,
}: IntroductionLetterSectionProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [value, setValue] = useState(initialValue ?? "");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [defaultApplied, setDefaultApplied] = useState(false);

  useEffect(() => {
    onChange?.(value);
  }, [value, onChange]);

  useEffect(() => {
    if (defaultApplied || value) return;
    if (!proposalData?.clientName && !proposalData?.deceasedName) return;

    const applyDefault = async () => {
      try {
        const res = await apiService.get<IntroductionLetterTemplate[]>(
          "/api/configurations/introduction-letter-templates/",
        );
        const category = bookingType.toLowerCase();
        const defaultTemplate = res.data.find(
          (t) =>
            t.category === category &&
            t.is_default_template === true &&
            t.is_active,
        );
        if (defaultTemplate) {
          setValue(
            stripHtml(
              replacePlaceholders(defaultTemplate.body_template, proposalData),
            ),
          );
          setDefaultApplied(true);
        }
      } catch (err) {
        console.error("Failed to apply default template:", err);
      }
    };

    applyDefault();
  }, [
    bookingType,
    proposalData?.clientName,
    proposalData?.deceasedName,
    proposalData,
    defaultApplied,
    value,
  ]);

  useEffect(() => {
    setDefaultApplied(false);
    setValue(initialValue ?? "");
  }, [bookingType, initialValue]);

  return (
    <Paper sx={{ borderRadius: "14px", p: 2.5, boxShadow: 0, mb: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1.5 }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "15px", color: colors.primary[100] }}>
          Introduction Letter
        </Typography>
        <Box
          onClick={() => setDialogOpen(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            border: `1px solid ${colors.primary[500]}`,
            borderRadius: "20px",
            px: 1.5,
            py: 0.5,
            cursor: "pointer",
            fontSize: "12px",
            color: colors.primary[500],
            fontWeight: 500,
            "&:hover": {  border: `1px solid ${colors.primary[400]}` },
          }}
        >
          <SwapHorizRounded sx={{ fontSize: 15 }} />
          Choose Template
        </Box>
      </Stack>

      <TextField
        multiline
        minRows={6}
        fullWidth
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Enter your introduction letter or choose a template..."
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            bgcolor: "background.paper",
            fontSize: "13px",
            color: colors.primary[100],
            lineHeight: 1.8,
            "& fieldset": { border: `1px solid ${colors.primary[500]}` },
            "&:hover fieldset": { borderColor: colors.primary[400] },
            "&.Mui-focused fieldset": { borderColor: colors.primary[400], borderWidth: 1 },
          },
        }}
      />

      <TemplatePickerDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onApply={(body) => {
          setValue(body);
          setDefaultApplied(true);
        }}
        bookingType={bookingType}
        proposalData={proposalData}
      />
    </Paper>
  );
}